import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { ContainersService } from 'src/app/providers/services/containers.service';
import { MarketService } from 'src/app/providers/services/market.service';
import { Container } from 'src/app/storage/models/container.model';
import { Market } from 'src/app/storage/models/market.model';

@Component({
    selector: 'app-containers',
    templateUrl: './containers.component.html',
    styleUrls: ['./containers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContainersComponent implements OnInit, OnDestroy {
  selectedMarket: Market;
  selectedMarketSubscription: Subscription;

  textFilter: string = "";

  readonly pageSize = 10;
  currentPage = 1;

  containers: Container[];
  displayedContainers: Container[];
  addingContainer: boolean = false;

  get paginatedContainers(): Container[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return (this.displayedContainers ?? []).slice(start, start + this.pageSize);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil((this.displayedContainers?.length ?? 0) / this.pageSize));
  }

  constructor(
    private translateService: TranslateService,
    private containersService: ContainersService,
    private confirmationModalService: ConfirmationModalService,
    private marketService: MarketService
  ) { }

  async ngOnInit(): Promise<void> {
    this.selectedMarketSubscription = this.marketService.selectedMarket$.subscribe(async selectedMarket => {
      if (selectedMarket) {
        this.selectedMarket = selectedMarket;
        await this.getContainers();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.selectedMarketSubscription) { this.selectedMarketSubscription.unsubscribe(); }
  }

  async getContainers(): Promise<void> {
    const containers = await this.containersService.getMarketContainers(this.selectedMarket.marketname);

    if (containers != null) {
      this.containers = containers;
      this.filterContainers();
    }
  }

  filterContainers(resetPage = true): void {
    if (resetPage) { this.currentPage = 1; }
    const filteredContainers = (this.containers ?? [])
      .filter(container => {
        // List attribute to filter on
        const attributesToFilterOn = [
          container.containerId,
          container.transportcompanyName
        ];

        // Find at least one attribute that checks the criterion
        return attributesToFilterOn.find(attribute =>
          attribute.toUpperCase().includes(this.textFilter.toUpperCase())
        ) != null;
      })

    const activatedContainers = filteredContainers
          .filter(container => !(container.deletionFlag))
          .sort((a, b) => a.containerId > b.containerId ? 1 : -1);
    const deactivatedContainers = filteredContainers
            .filter(container => container.deletionFlag)
            .sort((a, b) => a.containerId > b.containerId ? 1 : -1);

    this.displayedContainers = [...activatedContainers, ...deactivatedContainers];
  }

  setPage(page: number): void {
    this.currentPage = Math.min(Math.max(1, page), this.pageCount);
  }

  private displayUpdatedContainer(updatedContainer: Container): void {
    this.containers = this.containers.map(container => 
      container.containerpk === updatedContainer.containerpk ? new Container(updatedContainer) : container
    );
  }

  openContainerModal(): void {
    this.addingContainer = true;
  }

  async closeContainerModal(newContainer?: Container): Promise<void> {
    this.addingContainer = false;
    if (newContainer != null) { await this.getContainers(); }
  }

  async toggleContainerActivation(container: Container): Promise<void> {
    const updatedContainer = new Container(container);
    updatedContainer.toggleActivation();
    const isContainerUpdated = await this.containersService.updateContainer(this.selectedMarket.marketname, updatedContainer);

    // Set deletionFlag or reset to current value
    if (isContainerUpdated) { container.toggleActivation(); }

    this.displayUpdatedContainer(container);
  }

  async removeContainer(containerToRemove: Container): Promise<void> {
    const translations = await this.translateService.get('CONTAINERS').toPromise();

    const title = `<strong>${translations['REMOVE_CONTAINER']}</strong>`.replace('{CONTAINER_ID}', containerToRemove.containerId);

    // Open confirmation modal
    this.confirmationModalService.openModal({
      title: title,
      buttons: [
        {
          text: await this.translateService.get('CONFIRM').toPromise(),
          method: async () => {
            this.confirmationModalService.closeModal();
            const removed = await this.containersService.removeContainer(this.selectedMarket.marketname, containerToRemove);

            if (removed) {
              this.containers = this.containers.filter(container => containerToRemove.containerpk !== container.containerpk);
              this.filterContainers(false);
              this.setPage(this.currentPage);
            }
          }
        },
        {
          text: await this.translateService.get('CANCEL').toPromise(),
          method: async () => { this.confirmationModalService.closeModal(); }
        }
      ]
    });
  }

}
